import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Sparkles, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useActiveJob, getActiveJob, updateActiveJob, clearActiveJob, markQuoteNew } from "../../lib/analysisJobs";
import { deleteQuoteDraft } from "../../lib/draftStore";
import { track, debugServerEvent } from "../../lib/analytics";
import { trackAdsConversion } from "../../lib/gtagConversions";
import { useElapsed, analysisProgress } from "../quote/AnalysisProgress";

const POLL_MS = 2500;
const MAX_MS = 10 * 60 * 1000;

function JobToast({ id, tone, title, body, cta, to, navigate }) {
  const ok = tone === "ok";
  return (
    <div className={`w-[340px] max-w-[calc(100vw-2rem)] rounded-lg border p-4 shadow-xl bg-slate-950 ${ok ? "border-emerald-500/50" : "border-red-500/50"}`}
      data-testid={ok ? "quote-ready-toast" : "quote-failed-toast"}>
      <div className="flex items-start gap-3">
        {ok ? <CheckCircle2 size={18} className="text-emerald-400 mt-0.5"/> : <AlertTriangle size={18} className="text-red-400 mt-0.5"/>}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">{title}</div>
          <div className="text-xs text-slate-400 mt-1">{body}</div>
          <button type="button" onClick={() => { toast.dismiss(id); navigate(to); }}
            className="mt-3 text-xs font-medium px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white"
            data-testid={ok ? "quote-ready-open" : "quote-failed-retry"}>
            {cta}
          </button>
        </div>
        <button type="button" onClick={() => toast.dismiss(id)} className="text-slate-500 hover:text-slate-300" aria-label="close" data-testid="job-toast-close">
          <X size={14}/>
        </button>
      </div>
    </div>
  );
}

function AnalysisPill({ job, t }) {
  const elapsed = useElapsed(job.startedAt);
  const { label, pct, remainingS } = analysisProgress(job.stage, elapsed);
  return (
    <Link to="/new-quote" data-testid="analysis-job-pill"
      className="fixed z-30 bottom-4 left-4 right-24 sm:right-auto sm:w-72 lg:left-[272px] rounded-lg border border-blue-500/40 bg-slate-950/95 backdrop-blur px-4 py-3 shadow-xl hover:border-blue-400">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-blue-300 font-medium"><Sparkles size={12} className="animate-pulse"/> {t("analysis.inProgress")}</span>
        <span className="font-mono-data text-slate-400">{remainingS > 0 ? t("analysis.remaining", { s: remainingS }) : t("analysis.almostDone")}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full bg-blue-500 transition-[width] duration-700" style={{ width: `${pct}%` }}/>
      </div>
      <div className="mt-1.5 text-[11px] text-slate-500 truncate">{t(label)}</div>
    </Link>
  );
}

// App-level poller for the running analysis: it keeps going while the user
// browses other pages and resumes after a reload (job persisted locally).
export default function AnalysisJobWatcher() {
  const { user } = useAuth();
  const job = useActiveJob(user?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const live = useRef({});
  live.current = { path: location.pathname, t, navigate };

  const jobId = job?.status === "processing" ? job.jobId : null;
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    let timer;
    const showToast = (props) => toast.custom((id) => <JobToast id={id} navigate={live.current.navigate} {...props}/>, { duration: 20000 });

    const finish = (st, cur) => {
      clearActiveJob();
      deleteQuoteDraft(cur.userId);
      markQuoteNew(st.quote_id);
      trackAdsConversion("generate_lead", { currency: "EUR", value: st.total_eur || 0 });
      debugServerEvent("quote_generated", { quote_id: st.quote_id, total_amount: st.total_amount, currency: st.currency, total_eur: st.total_eur });
      window.dispatchEvent(new CustomEvent("mp-quote-ready", { detail: { quoteId: st.quote_id } }));
      const { path, t: tr, navigate: nav } = live.current;
      if (path === "/new-quote") return nav(`/quotes/${st.quote_id}`);
      showToast({ tone: "ok", title: tr("analysis.readyTitle"), body: tr("analysis.readyBody"), cta: tr("analysis.open"), to: `/quotes/${st.quote_id}` });
    };

    const fail = (message, reason, cur) => {
      updateActiveJob({ status: "failed", error: message });
      track("quote_generation_failed", { reason, elapsed_ms: Date.now() - cur.startedAt });
      const { path, t: tr } = live.current;
      if (path !== "/new-quote") showToast({ tone: "err", title: tr("analysis.failedTitle"), body: message, cta: tr("analysis.retry"), to: "/new-quote" });
    };

    const tick = async () => {
      const cur = getActiveJob();
      if (cancelled || !cur || cur.jobId !== jobId) return;
      try {
        const { data: st } = await api.get(`/quotes/analyze-jobs/${jobId}`, { timeout: 15000 });
        if (cancelled) return;
        if (st.status === "completed") return finish(st, cur);
        if (st.status === "failed") return fail(formatApiError(st.error) || live.current.t("newQuote.errorAnalysis"), "job_failed", cur);
        if (st.stage && st.stage !== cur.stage) updateActiveJob({ stage: st.stage });
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 404) return fail(live.current.t("analysis.lost"), "not_found", cur);
      }
      if (Date.now() - cur.startedAt > MAX_MS) return fail(live.current.t("analysis.timeout"), "timeout", cur);
      timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [jobId]);

  if (!jobId || location.pathname === "/new-quote") return null;
  return <AnalysisPill job={job} t={t}/>;
}
